<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  /** Percent change against the preceding equal-length window, or null when not comparing. */
  changePercent: {
    type: Number,
    default: null,
  },
  /** Wording for the change when a percentage would mislead, such as a rate moving in points. */
  changeLabel: {
    type: String,
    default: '',
  },
  tone: {
    type: String,
    default: 'teal',
  },
  /** One line saying what the figure counts. */
  hint: {
    type: String,
    default: '',
  },
});

const hasChange = computed(() => props.changePercent !== null && props.changePercent !== undefined);
const isUp = computed(() => hasChange.value && props.changePercent > 0);
const isFlat = computed(() => hasChange.value && props.changePercent === 0);

const changeText = computed(() => {
  if (!hasChange.value) {
    return '';
  }
  if (props.changeLabel) {
    return props.changeLabel;
  }
  const sign = props.changePercent > 0 ? '+' : '';
  return `${sign}${props.changePercent.toFixed(1)}%`;
});
</script>

<template>
  <div :class="$style.tile">
    <span :class="[$style.label, $style[tone]]">{{ label }}</span>
    <strong :class="$style.value">{{ value }}</strong>
    <span
      v-if="hasChange"
      :class="[$style.change, isFlat ? $style.flat : (isUp ? $style.up : $style.down)]"
    >
      <span aria-hidden="true">{{ isFlat ? '→' : (isUp ? '↗' : '↘') }}</span>
      {{ changeText }}
    </span>
    <span v-if="hint" :class="$style.hint">{{ hint }}</span>
  </div>
</template>

<style module>
.tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 18px;
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  min-width: 0;
}
.label {
  font-size: var(--font-size-lg);
  font-weight: 500;
}
.teal { color: var(--pfs-teal); }
.orange { color: var(--orange-dark); }
.red { color: var(--pfs-red); }

.value {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--primary-font-dark-color);
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
}
.change {
  font-size: var(--font-size-lg);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.hint {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.up { color: var(--green-light); }
.down { color: var(--button-red); }
.flat { color: var(--faint-color); }
</style>
